import axios from "axios";

// Google Geocoding API configuration
const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const GOOGLE_GEOCODING_URL = "https://maps.googleapis.com/maps/api/geocode/json";

/**
 * Extract street address using Google Geocoding API
 * @param {string} fullAddress - The full address to parse
 * @returns {Promise<Object>} - Object containing street address and additional details
 */
export const extractStreetAddressWithGoogle = async (fullAddress, type) => {
	if (!fullAddress || !GOOGLE_API_KEY) {
		return {
			streetAddress: "",
			postalCode: "",
			formattedAddress: "",
			components: {},
			success: false,
		};
	}

	try {
		const response = await axios.get(GOOGLE_GEOCODING_URL, {
			params: {
				address: fullAddress,
				key: GOOGLE_API_KEY,
				// components: "country:IN", // Restrict to India for better results
			},
		});

		if (response.data.status === "OK" && response.data.results.length > 0) {
			const result = response.data.results[0];
			const addressComponents = result.address_components;

			// Extract street address components
			const streetNumber = addressComponents.find((comp) => comp.types.includes("street_number"))?.long_name || "";

			const route = addressComponents.find((comp) => comp.types.includes("route"))?.long_name || "";

			const sublocality = addressComponents.find((comp) => comp.types.includes("sublocality_level_1"))?.long_name || "";

			const locality = addressComponents.find((comp) => comp.types.includes("locality"))?.long_name || "";

			const postalCode = addressComponents.find((comp) => comp.types.includes("postal_code"))?.long_name || "";

			let streetAddress;

			streetAddress = [route].filter(Boolean).join(" ").trim();

			return {
				streetAddress: streetAddress || fullAddress,
				// formattedAddress: result.formatted_address?.trim(),
				formattedAddress: route?.trim() || result.formatted_address?.trim(),
				postalCode: postalCode,
				components: {
					// streetNumber,
					route,
					sublocality,
					locality,
					// fullAddress: result.formatted_address
					// 	?.replace(/\d+/g, "")
					// 	?.replace(/\s{2,}/g, " ")
					// 	?.trim(),
					fullAddress: route?.trim() || result.formatted_address?.trim(),
					postalCode,
				},
				success: true,
			};
		} else {
			console.warn("Google Geocoding API returned no results");
			return {
				streetAddress: fullAddress,
				formattedAddress: fullAddress,
				components: {},
				success: false,
			};
		}
	} catch (error) {
		console.error("Error calling Google Geocoding API:", error);
		return {
			streetAddress: fullAddress,
			formattedAddress: fullAddress,
			components: {},
			success: false,
		};
	}
};

/**
 * Fallback function for when Google API is not available
 * @param {string} fullAddress - The full address to parse
 * @returns {string} - Extracted street address
 */
export const extractStreetAddressFallback = (fullAddress) => {
	if (!fullAddress) return "";

	// Split by comma and look for street-like patterns
	const parts = fullAddress.split(",");

	// Special handling for complex addresses like "TWINSTAR, 150 Feet Ring Rd, near Nana Mava Road..."
	// Look for the part that contains both numbers and street indicators
	for (let i = 0; i < parts.length; i++) {
		const part = parts[i].trim();

		// Check if this part looks like a street address
		if (
			part.length > 5 &&
			(part.includes("Road") ||
				part.includes("Rd") ||
				part.includes("Street") ||
				part.includes("St") ||
				part.includes("Avenue") ||
				part.includes("Ave") ||
				part.includes("Lane") ||
				part.includes("Ln") ||
				part.includes("Drive") ||
				part.includes("Dr") ||
				part.includes("Boulevard") ||
				part.includes("Blvd") ||
				part.includes("Circle") ||
				part.includes("Cir") ||
				part.includes("Feet") ||
				part.includes("ft") ||
				part.includes("Ring") ||
				part.includes("Society") ||
				/\d/.test(part)) // Contains numbers
		) {
			// For addresses like "150 Feet Ring Rd", return the full part
			if (part.includes("Feet") && part.includes("Ring") && part.includes("Rd")) {
				return part;
			}
			// For other street patterns, return the part
			return part;
		}
	}

	// Fallback: return the first meaningful part
	if (parts.length > 0) {
		const firstPart = parts[0].trim();
		if (firstPart.length > 3) {
			return firstPart;
		}
	}

	return fullAddress.trim();
};
